import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { analyticsAutoExportService } from "@/services/analytics-auto-export-service";
import type {
  CreateAutoExportScheduleBody,
  ListAutoExportSchedulesQuery,
  UpdateAutoExportScheduleBody,
} from "@/types/analytics-auto-export";

export const autoExportKeys = {
  all: ["analytics-auto-export"] as const,
  list: (params?: ListAutoExportSchedulesQuery) =>
    [...autoExportKeys.all, "list", params ?? {}] as const,
};

export function useAutoExportSchedules(
  params?: ListAutoExportSchedulesQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: autoExportKeys.list(params),
    queryFn: () => analyticsAutoExportService.list(params),
    enabled,
  });
}

export function useCreateAutoExportSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateAutoExportScheduleBody) =>
      analyticsAutoExportService.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: autoExportKeys.all });
    },
  });
}

export function useUpdateAutoExportSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateAutoExportScheduleBody;
    }) => analyticsAutoExportService.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: autoExportKeys.all });
    },
  });
}

export function useDeleteAutoExportSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => analyticsAutoExportService.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: autoExportKeys.all });
    },
  });
}

export function useRunAutoExportNow() {
  return useMutation({
    mutationFn: (id: string) => analyticsAutoExportService.runNow(id),
  });
}
